<template>
  <div
    class="dict-search-wrap ga-navbar-search ga-navbar-search--with-icon"
    :data-trending-label="showTrendingHeader ? $t('dict.trending') : ''"
  >
    <i class="fas fa-search ga-navbar-search__icon" aria-hidden="true"></i>
    <v-select
      ref="select"
      :filterable="false"
      :clearable="false"
      taggable
      :placeholder="placeholder"
      :create-option="createSearchOption"
      :get-option-label="optionLabel"
      :reduce="reduceOption"
      :options="autocompletionOptions"
      @input="onSearchSelected"
      @search="onSearch"
      @open="onOpen"
      v-model="selectedTerm"
    >
      <template #option="option">
        <div :class="['dict-select-option', option.trending ? 'dict-select-option--trending' : '']">
          <i
            v-if="option.trending"
            class="fas fa-chart-line dict-trending-icon"
            aria-hidden="true"
          ></i>
          <span class="dict-select-option__term">{{ optionLabel(option) }}</span>
          <span class="dict-select-option__hint" v-if="option.code && option.code.info">{{ option.code.info }}</span>
          <span class="dict-select-option__alt" v-if="option.label">{{ option.label }}</span>
        </div>
      </template>
      <template slot="no-options">
        <div class="dict-select-loading" v-if="autocompleteloading || trendingLoading">
          <b-spinner small variant="secondary"></b-spinner>
          <span>{{ $t('dict.searching') }}</span>
        </div>
        <div class="dict-select-no-results" v-else-if="activeQuery">
          {{ $t('dict.search_press_enter') }}
        </div>
      </template>
    </v-select>
  </div>
</template>

<script>
import { DictionaryEntryService } from '../services/dictionaryEntryService'
import { listMixin } from '../mixins/listMixin'
import { compareMixin } from '../mixins/compareMixin'
import { entryNeutralLabel, entryTrendingSearchTerm, entryIsTrendingNeutral } from '../utils/entrySearchLabel'

const TRENDING_LIMIT = 8

export default {
  name: 'DictSearchInput',
  mixins: [listMixin, compareMixin],
  data () {
    return {
      autocompleteloading: false,
      trendingLoading: false,
      showTrendingHeader: false,
      selectedTerm: '',
      activeQuery: ''
    }
  },
  computed: {
    placeholder () {
      return this.$t('dict.navbar_search_placeholder')
    },
    routeSearchTerm () {
      return this.$route.query.search || this.$route.params.specific || ''
    }
  },
  watch: {
    routeSearchTerm: {
      immediate: true,
      handler (term) {
        this.selectedTerm = term || ''
      }
    }
  },
  methods: {
    optionLabel (option) {
      if (!option) return ''
      if (typeof option === 'string') return option
      if (option.code && option.code.c) return option.code.c
      if (option.displayLabel) return option.displayLabel
      return ''
    },
    reduceOption (option) {
      if (!option) return ''
      if (typeof option === 'string') return option
      return option.code && option.code.c ? option.code.c : ''
    },
    createSearchOption (word) {
      return { code: { c: word, info: this.$t(' - gender app search') } }
    },
    onOpen () {
      const query = (this.activeQuery || this.selectedTerm || this.routeSearchTerm || '').trim()
      if (!query) {
        this.fetchTrendingOptions()
        return
      }
      this.showTrendingHeader = false
      this.fetchAutocompletionOptions(query)
    },
    onSearch (searchArg) {
      this.activeQuery = typeof searchArg === 'string' ? searchArg : ''
      if (!this.activeQuery.trim()) {
        this.fetchTrendingOptions()
        return
      }
      this.showTrendingHeader = false
      this.fetchAutocompletionOptions(this.activeQuery)
    },
    onSearchSelected (term) {
      if (!term) return
      const query = { search: term }
      if (this.$route.path === '/' && this.$route.query.search === term) return
      this.$router.push({ path: '/', query }).catch(() => {})
    },
    fetchTrendingOptions () {
      const instance = this
      this.showTrendingHeader = true
      this.trendingLoading = true
      this.autocompletionOptions = []
      DictionaryEntryService.getTrendingTerms()
        .then((response) => {
          const seen = new Set()
          const options = []
          for (const entry of response.data.results || []) {
            if (!entryIsTrendingNeutral(entry)) continue
            const searchTerm = entryTrendingSearchTerm(entry)
            const neutral = entryNeutralLabel(entry)
            if (!searchTerm || seen.has(searchTerm.toLowerCase())) continue
            seen.add(searchTerm.toLowerCase())
            options.push({
              trending: true,
              code: { c: searchTerm },
              label: neutral,
              id: entry.id
            })
            if (options.length >= TRENDING_LIMIT) break
          }
          instance.autocompletionOptions = options
        })
        .catch((error) => {
          console.log(error)
          instance.showTrendingHeader = false
        })
        .finally(() => {
          instance.trendingLoading = false
        })
    },
    fetchAutocompletionOptions (searchArg) {
      const instance = this
      const search = typeof searchArg === 'string' ? searchArg : ''
      this.autocompletionOptions = []
      if (!search.trim()) {
        instance.autocompleteloading = false
        return
      }
      instance.autocompleteloading = true
      DictionaryEntryService.getAutoCompletionData(search.trim())
        .then((response) => {
          const datasets = response.data.results
          const unsorted = []
          for (const data of datasets) {
            const _option = this.prepareAutoCompleteOptions(data)
            unsorted.push(..._option)
          }
          const unique = []
          const map = new Map()
          for (const item of unsorted) {
            if (!map.has(item.code.c)) {
              map.set(item.code.c, true)
              unique.push({
                code: item.code,
                label: item.label,
                id: item.id
              })
            }
          }
          this.autocompletionOptions = unique.sort(function (a, b) {
            if (!a.code || !b.code || !search) return 0
            if (!a.code.c || !b.code.c) return 0
            return instance.jaroWinkerStringSimilarity(b.code.c, search) - instance.jaroWinkerStringSimilarity(a.code.c, search)
          })
        })
        .catch((error) => {
          console.log(error)
        })
        .finally(() => {
          instance.autocompleteloading = false
        })
    }
  }
}
</script>
